import { MailWarning } from "lucide-react";
import { api } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { AsyncButton } from "../ui/async-button.js";
import type { LightboxTarget } from "../DocumentLightbox.js";

/**
 * HRMS-0634: HR's one-click way to tell the employee their uploaded document
 * isn't readable / isn't suitable, and to please upload another. Opens the
 * Document Lightbox pre-switched to reject mode with a doc-themed reason
 * pre-filled; the existing reject-with-reason route emails the employee,
 * writes a bell notification, reopens the onboarding record if they'd
 * submitted, and hands them a fresh wizard link.
 *
 * Lives on every HR-side card where the EMPLOYEE uploads the document.
 * Deliberately NOT shown on the Offer Letter card — HR uploads that one.
 */
export function AskForClearerButton({
  employeeId, docType, docLabel, isHR, setVerifyTarget, defaultReason,
}: {
  employeeId: string | null;
  docType: string;
  docLabel: string;
  isHR: boolean;
  setVerifyTarget: (t: LightboxTarget) => void;
  defaultReason?: string;
}) {
  const toast = useToast();
  if (!isHR || !employeeId) return null;

  async function askForClearer() {
    if (!employeeId) throw new Error("validation");
    try {
      const { data: docs } = await api.get<Array<{ id: string }>>(`/employees/${employeeId}/documents?type=${docType}`);
      const docId = docs[0]?.id;
      if (!docId) { toast.show(`No ${docLabel} uploaded yet.`, "warning"); return; }
      const { data } = await api.get<{ mimeType: string | null; versions: Array<{ id: string; version: number; extractedData: Record<string, unknown> | null; hrVerifiedAt: string | null; hrRejectedAt: string | null }> }>(`/documents/${docId}/versions`);
      const latest = data.versions[0];
      if (!latest) { toast.show(`No ${docLabel} uploaded yet.`, "warning"); return; }
      if (latest.hrVerifiedAt) {
        toast.show(`The latest ${docLabel} is already verified — upload a new one if you need to replace it.`, "info");
        return;
      }
      const currentStatus: LightboxTarget["currentStatus"] = latest.hrRejectedAt ? "REJECTED" : "UNVERIFIED";
      setVerifyTarget({
        versionId:           latest.id,
        docType,
        version:             latest.version,
        mimeType:            data.mimeType,
        extractedData:       latest.extractedData,
        currentStatus,
        isHR:                true,
        startInRejectMode:   true,
        initialRejectReason: defaultReason ?? `The ${docLabel} you uploaded isn't clear enough. Please upload a clearer copy.`,
      });
    } catch (err) {
      logError("AskForClearerButton", err, { docType });
      toast.show(`Could not open the ${docLabel} — please try again.`, "error");
      throw err;
    }
  }

  return (
    // Compact (28px, sized to its label) with a soft amber tint, so it reads as a small
    // secondary action under the dropzone rather than a second primary button.
    <AsyncButton
      variant="outline"
      animated={false}
      size="sm"
      className="mt-1.5 h-7 max-w-full gap-1.5 rounded-md border-[var(--color-warning)]/45 bg-[var(--color-warning-bg)] px-2.5 text-xs font-semibold text-[var(--color-warning-fg)] shadow-none hover:border-[var(--color-warning)] hover:bg-[var(--color-warning-bg)] hover:brightness-110"
      onClick={() => askForClearer()}
    >
      <MailWarning size={13} />
      <span className="min-w-0 truncate">Ask for a clearer {docLabel}</span>
    </AsyncButton>
  );
}
